/**
 * CollisionConfig - singleton managing global collision profiles and channels.
 */

import {
  CollisionChannel,
  CollisionMode,
  CollisionProfile,
  CollisionResponse,
  CollisionResponseCustomization,
  CustomChannelConfig,
  DefaultCollisionProfile
} from './CollisionTypes.js';

import type {
  ChannelResponse,
  CollisionConfigData,
  CollisionProfileData,
  CollisionResponseCustomizationData
  ,
  CustomChannelConfigData} from './CollisionTypes.js';

export class CollisionConfig {
  private static instance: CollisionConfig | null = null;

  /** Profiles which can be used to configure collision detection for components */
  public readonly profiles: Array<CollisionProfile>;
  public readonly customChannelConfigs: Array<CustomChannelConfig>;
  /** Overrides to apply to the collision profiles. Useful when you have added your own profiles and want to configure how they interact with the default profiles */
  public readonly profileOverrides: Array<CollisionResponseCustomization>;

  private constructor(config?: CollisionConfigData) {
    // Initialize custom channel configs first
    this.customChannelConfigs = this.createCustomChannelConfigs(config?.customChannelConfigs);

    // Create default profiles
    const defaultProfiles = this.createDefaultProfiles();

    // Process and create user profiles
    const processedUserProfiles = this.processUserProfiles(config?.userProfiles);

    // Process and create profile overrides
    const processedOverrides = this.processProfileOverrides(config?.profileOverrides);

    // Apply overrides to default profiles
    this.applyProfileOverrides(
      defaultProfiles,
      processedOverrides,
      this.customChannelConfigs
    );

    // Combine all profiles
    this.profiles = [
      ...defaultProfiles,
      ...processedUserProfiles
    ];

    // Store overrides
    this.profileOverrides = processedOverrides;
  }

  private createCustomChannelConfigs(configs?: Array<CustomChannelConfigData>): Array<CustomChannelConfig> {
    return (configs ?? []).map(c =>
      new CustomChannelConfig(c.channel, c.defaultResponse, c.name)
    );
  }

  private processUserProfiles(profiles?: Array<CollisionProfileData>): Array<CollisionProfile> {
    return (profiles ?? []).map(p => {
      const processedResponses = this.processChannelResponses(p.responses ?? []);
      return new CollisionProfile(
        p.name,
        p.mode,
        this.resolveChannelName(p.objectChannelName) ?? CollisionChannel.WorldStatic,
        processedResponses
      );
    });
  }

  private processProfileOverrides(overrides?: Array<CollisionResponseCustomizationData>): Array<CollisionResponseCustomization> {
    return (overrides ?? []).map(o => {
      const processedCustomizations = this.processChannelResponses(o.responseCustomizations);
      return new CollisionResponseCustomization(o.profileName, processedCustomizations);
    });
  }

  private processChannelResponses(responses: Array<ChannelResponse>): Array<ChannelResponse> {
    return responses.map(r => {
      const resolvedChannel = this.resolveChannelName(r.channel);
      return {
        ...r,
        channel: resolvedChannel ?? r.channel
      };
    });
  }

  private resolveChannelName(channelName: string): CollisionChannel | undefined {
    // Try to match by enum key
    const key = channelName as keyof typeof CollisionChannel;
    if (key in CollisionChannel) {
      return CollisionChannel[key];
    }
    // Try to match by value (stringified number)
    for (const key in CollisionChannel) {
      if (CollisionChannel[key as keyof typeof CollisionChannel].toString() === channelName) {
        return CollisionChannel[key as keyof typeof CollisionChannel];
      }
    }
    // Try to match by custom channel config name
    const customChannelConfig = this.customChannelConfigs.find(c => c.name === channelName);
    if (customChannelConfig) {
      return customChannelConfig.channel;
    }
    return undefined;
  }

  private createDefaultProfiles(): Array<CollisionProfile> {
    return [
      new CollisionProfile(DefaultCollisionProfile.NoCollision, CollisionMode.NoCollision, CollisionChannel.WorldStatic, []),
      new CollisionProfile(DefaultCollisionProfile.BlockAll, CollisionMode.QueryAndPhysics, CollisionChannel.WorldStatic, []),
      new CollisionProfile(DefaultCollisionProfile.OverlapAll, CollisionMode.QueryOnly, CollisionChannel.WorldStatic, [
        { channel: CollisionChannel.WorldStatic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.WorldDynamic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Visibility, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Camera, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.PhysicsBody, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Destructible, response: CollisionResponse.Overlap },
      ]),
      new CollisionProfile(DefaultCollisionProfile.BlockAllDynamic, CollisionMode.QueryAndPhysics, CollisionChannel.WorldDynamic, []),
      new CollisionProfile(DefaultCollisionProfile.OverlapAllDynamic, CollisionMode.QueryOnly, CollisionChannel.WorldDynamic, [
        { channel: CollisionChannel.WorldStatic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.WorldDynamic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Camera, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.PhysicsBody, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Destructible, response: CollisionResponse.Overlap },
      ]),
      new CollisionProfile(DefaultCollisionProfile.IgnoreOnlyPawns, CollisionMode.QueryOnly, CollisionChannel.WorldDynamic, [
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Ignore },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Ignore },
      ]),
      new CollisionProfile(DefaultCollisionProfile.Pawn, CollisionMode.QueryAndPhysics, CollisionChannel.Pawn, [
        { channel: CollisionChannel.Visibility, response: CollisionResponse.Ignore },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Camera, response: CollisionResponse.Ignore },
      ]),
      new CollisionProfile(DefaultCollisionProfile.Character, CollisionMode.QueryAndPhysics, CollisionChannel.Pawn, [
        { channel: CollisionChannel.Visibility, response: CollisionResponse.Ignore },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Camera, response: CollisionResponse.Ignore },
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Ignore },
      ]),
      new CollisionProfile(DefaultCollisionProfile.Destructible, CollisionMode.QueryAndPhysics, CollisionChannel.Destructible, []),
      new CollisionProfile(DefaultCollisionProfile.InvisibleWall, CollisionMode.QueryAndPhysics, CollisionChannel.WorldStatic, []),
      new CollisionProfile(DefaultCollisionProfile.InvisibleWallDynamic, CollisionMode.QueryAndPhysics, CollisionChannel.WorldDynamic, []),
      new CollisionProfile(DefaultCollisionProfile.Trigger, CollisionMode.QueryOnly, CollisionChannel.WorldDynamic, [
        { channel: CollisionChannel.WorldStatic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.WorldDynamic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.PhysicsBody, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Destructible, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
      ]),
      new CollisionProfile(DefaultCollisionProfile.Ragdoll, CollisionMode.QueryAndPhysics, CollisionChannel.PhysicsBody, [
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Ignore },
      ]),
      new CollisionProfile(DefaultCollisionProfile.Vehicle, CollisionMode.QueryAndPhysics, CollisionChannel.Vehicle, []),
      new CollisionProfile(DefaultCollisionProfile.UI, CollisionMode.QueryOnly, CollisionChannel.WorldDynamic, [
        { channel: CollisionChannel.WorldStatic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.WorldDynamic, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Visibility, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Camera, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.PhysicsBody, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Vehicle, response: CollisionResponse.Overlap },
        { channel: CollisionChannel.Destructible, response: CollisionResponse.Overlap },
      ]),
      new CollisionProfile(DefaultCollisionProfile.SpringArmCamera, CollisionMode.QueryOnly, CollisionChannel.Camera, [
        // Ignore Pawn channel so both Pawn and Character profiles are ignored by default.
        { channel: CollisionChannel.Pawn, response: CollisionResponse.Ignore },
      ]),
    ];
  }

  private applyProfileOverrides(
    profiles: Array<CollisionProfile>,
    overrides: Array<CollisionResponseCustomization>,
    customChannelConfigs: Array<CustomChannelConfig>
  ): void {
    overrides.forEach(override => {
      const profile = profiles.find(p => p.name === override.profileName);
      if (profile) {
        override.responseCustomizations.forEach(customization => {
          const resolvedChannel = this.resolveChannelName(customization.channel);
          if (resolvedChannel) {
            const index = profile.responses.findIndex(r => r.channel === resolvedChannel);
            if (index === -1) {
              profile.responses.push({ channel: resolvedChannel, response: customization.response });
            } else {
              profile.responses[index] = { channel: resolvedChannel, response: customization.response };
            }
          }
        });
      }
    });
  }

  /**
   * Gets the singleton instance of CollisionConfig
   * @returns The CollisionConfig instance
   */
  public static getInstance(): CollisionConfig {
    if (!CollisionConfig.instance) {
      CollisionConfig.setup();
    }
    return CollisionConfig.instance!;
  }

  /**
   * Initializes the CollisionConfig singleton with optional custom configuration
   * @param config Optional configuration to override defaults
   * @throws Error if setup() has already been called
   */
  public static setup(config?: CollisionConfigData): void {
    if (CollisionConfig.instance) {
      console.log('Replacing existing CollisionConfig');
    }

    CollisionConfig.instance = new CollisionConfig(config);
  }

  /**
   * Gets a collision profile by name
   * @param name The name of the profile to find
   * @returns The collision profile or undefined if not found
   */
  public getProfile(name: string): CollisionProfile | undefined {
    return this.profiles.find(profile => profile.name === name);
  }
}

/**
 * Resolves a channel name to a CollisionChannel enum value
 * @param channel The channel name to resolve
 * @returns The resolved CollisionChannel or undefined if not found
 */
export function resolveChannelName(channel: string): CollisionChannel | undefined {
  // Try to match by enum key
  const key = channel as keyof typeof CollisionChannel;
  if (key in CollisionChannel) {
    return CollisionChannel[key];
  }
  // Try to match by value (stringified number)
  for (const key in CollisionChannel) {
    if (CollisionChannel[key as keyof typeof CollisionChannel].toString() === channel) {
      return CollisionChannel[key as keyof typeof CollisionChannel];
    }
  }
  // Try to match by custom channel config name
  const customChannelConfig = CollisionConfig.getInstance().customChannelConfigs.find(config => config.name === channel);
  if (customChannelConfig) {
    return customChannelConfig.channel;
  }
  return undefined;
}

export function getCollisionChannelIndex(channel: CollisionChannel): number {
  return Object.values(CollisionChannel).indexOf(channel);
}

export function getCollisionChannelBit(channel: CollisionChannel): number {
  return 1 << getCollisionChannelIndex(channel);
}
